import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { categories } from "@/mocks/categories";

const genreName = (key: string) =>
  categories.find((c) => c.key === key)?.name ?? key;

interface MovieCardProps {
  movie: Movie;
  showExcerpt?: boolean;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
}

export default function MovieCard({
  movie,
  showExcerpt = false,
  isFavorite,
  onToggleFavorite,
}: MovieCardProps) {
  return (
    <article className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
      <Link
        to={`/detail/${movie.id}`}
        className="relative block w-full aspect-[2/3] overflow-hidden bg-background-200"
      >
        <img
          src={movie.poster}
          alt={`${movie.title} 电影海报`}
          title={`${movie.title} ${movie.titleEn} 欧美电影`}
          className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-2 left-2 px-2 py-1 rounded-md bg-primary-950/80 text-background-50 text-xs font-label font-semibold backdrop-blur-sm">
          {movie.year}
        </span>
        <span className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md bg-accent-500 text-primary-950 text-xs font-bold font-label">
          <i className="ri-star-fill text-[10px]" />
          {movie.rating.toFixed(1)}
        </span>
      </Link>

      <div className="flex flex-col flex-1 p-3 md:p-4">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/detail/${movie.id}`}
            className="font-heading font-bold text-sm md:text-base text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer line-clamp-2"
          >
            {movie.title}
          </Link>
          {onToggleFavorite && (
            <button
              type="button"
              onClick={() => onToggleFavorite(movie.id)}
              aria-label={isFavorite ? "取消收藏" : "收藏"}
              className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                isFavorite
                  ? "text-accent-600 bg-accent-50"
                  : "text-foreground-400 hover:text-accent-600 hover:bg-background-100"
              }`}
            >
              <i className={isFavorite ? "ri-heart-3-fill" : "ri-heart-3-line"} />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {movie.genres.slice(0, 2).map((g) => (
            <span
              key={g}
              className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[11px] font-medium"
            >
              {genreName(g)}
            </span>
          ))}
        </div>

        {showExcerpt && (
          <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
            {movie.reviewExcerpt}
          </p>
        )}
      </div>
    </article>
  );
}